/**
 * Formatting helpers for the AI usage dashboard.
 *
 * Every cost formatter in this file treats `null` as "no cost applies"
 * (local-model usage) and renders an em dash -- never "$0.00". Nothing
 * here ever coalesces a null cost to 0 for display.
 */

/**
 * Two decimals for money people recognise as money, more for amounts
 * smaller than a cent.
 *
 * Per-request AI spend is genuinely sub-cent -- a real recorded request on
 * the live deployment cost $0.000306 -- and rounding that to "$0.00" made
 * a working cost pipeline look like a broken one: every row, every tile
 * and every axis label on the dashboard read zero while real money was
 * being spent. Six digits is where Postgres stops too (`cost_usd` is
 * `Numeric(12, 6)`), so nothing is displayed that isn't stored.
 */
export function formatMoney(value: number): string {
  const subCent = value !== 0 && Math.abs(value) < 0.01;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: subCent ? 6 : 2,
  })}`;
}

/** `null` in, em dash out. This is the ONLY correct way to render `costUsd`. */
export function formatCost(value: number | null): string {
  return value === null ? '—' : formatMoney(value);
}

export function formatTokens(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${String(Math.round(value / 1e3))}K`;
  return value.toLocaleString('en-US');
}

export function formatRequests(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * Sums a set of nullable per-row costs the way the product rules require:
 * - every value null (an all-local group) -> `null` (renders as em dash)
 * - a mix of null and real values -> the sum of the real ones (the null
 *   rows contribute nothing, because they genuinely have no cost -- this
 *   is not "unknown data", it is "zero rows that have a price at all")
 *
 * Never write `values.reduce((a, v) => a + (v ?? 0), 0)` instead of this --
 * that silently turns an all-null group into `$0.00`.
 */
export function sumNullableCost(values: Array<number | null>): number | null {
  const real = values.filter((value): value is number => value !== null);
  if (real.length === 0) return null;
  return real.reduce((total, value) => total + value, 0);
}
